
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AutocompleteFieldComponent } from 'src/app/components/form/autocomplete-field/autocomplete-field.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { FormService } from 'src/app/services/form.service';

@Component({
  selector: 'settings-projects-checklist-form',
  standalone: true,
  imports: [ReactiveFormsModule, NgbAlert, InputFieldComponent, SelectFieldComponent, AutocompleteFieldComponent],
  templateUrl: './settings-projects-checklist-form.component.html',
  styleUrl: './settings-projects-checklist-form.component.scss'
})
export class SettingsProjectsChecklistFormComponent implements OnInit {

  @Input() checklist?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  @Output() onFieldChange = new EventEmitter<{ jurisdiction: number; law: number }>();

  form!: FormGroup; 

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  jurisdictionsOptions: { key: string; value: string }[] = [];
  lawsOptions: { key: number; value: string }[] = [];
  checklistGroupOptions: { key: number; value: string }[] = [];
  selectedChecklistGroupId: number | null = null;


  constructor(
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    public projectsService: ProjectsService,
  ) {
    this.projectsService.jurisdictions$.subscribe({
      next: (jurisdictions: any) => {
        if (!jurisdictions || !Array.isArray(jurisdictions)) {
          console.warn('Received invalid jurisdictions data:', jurisdictions);
          this.jurisdictionsOptions = [];
          return;
        }
        
        const validJurisdictions = jurisdictions.filter((jurisdiction: any) => 
          jurisdiction && jurisdiction.jurisdictionId !== undefined
        );
        
        this.jurisdictionsOptions = validJurisdictions.map((jurisdiction: any) => ({
          key: String(jurisdiction.jurisdictionId), 
          value: jurisdiction.name || `Jurisdiction ${jurisdiction.jurisdictionId}`
        }));
      },
      error: (error) => console.error('Error loading jurisdictions:', error)
    });
    
    this.projectsService.laws$.subscribe({
      next: (laws: any) => {
        if (!laws || !Array.isArray(laws)) {
          console.warn('Received invalid laws data:', laws);
          this.lawsOptions = [];
          return;
        }
        
        const validLaws = laws.filter((law: any) => 
          law && (law.lawId !== undefined || law.id !== undefined)
        );
        
        this.lawsOptions = validLaws.map((law: any) => {
          const lawId = law.lawId || law.id;
          return { 
            key: Number(lawId), 
            value: law.name || `Law ${lawId}`
          };
        });
        
        console.log('Loaded laws options:', this.lawsOptions);
      },
      error: (error) => console.error('Error loading laws:', error)
    });
  }


  ngOnInit(): void {
    this.projectsService.getLaws();
    this.loadChecklistGroups();

    this.form = new FormGroup({
      jurisdictionId: new FormControl('', [Validators.required]),
      law: new FormControl(''),
      checklistGroupId: new FormControl('', [Validators.required]),
      revision: new FormControl('', [Validators.required, Validators.min(0)]),
      details: new FormControl('', [Validators.required]),
    });

    this.form.get('jurisdictionId')?.valueChanges.subscribe(selectedJurisdiction => {
      if (selectedJurisdiction) {
        const currentLaw = this.form.get('law')?.value;
        
        this.onFieldChange.emit({ 
          jurisdiction: selectedJurisdiction, 
          law: currentLaw || 0 
        });
        
        this.projectsService.getLawsByJurisdiction(selectedJurisdiction).subscribe({
          next: (laws: any[]) => {
            if (laws && laws.length > 0) {
              if (laws[0].key !== undefined && laws[0].value !== undefined) {
                this.lawsOptions = laws;
              } else {
                this.lawsOptions = laws.map(law => ({ 
                  key: law.lawId || law.id, 
                  value: law.name 
                }));
              }
              
              
              const currentLaw = this.form.get('law')?.value;
              const lawExists = this.lawsOptions.some(option => option.key == currentLaw);
              
              if (!lawExists) {
                this.form.get('law')?.setValue('');
              }
            } else {
              this.lawsOptions = [];
              this.form.get('law')?.setValue('');
              console.warn('No laws found for jurisdiction:', selectedJurisdiction);
            }
          },
          error: (error) => {
            console.error('Error loading laws for jurisdiction:', selectedJurisdiction, error);
            this.alertNotificationService.error('Failed to load laws for the selected jurisdiction');
            this.lawsOptions = [];
          }
        });
      }
    });

    this.form.get('law')?.valueChanges.subscribe((law) => {
      const jurisdiction = this.form.get('jurisdictionId')?.value;
      
      if (jurisdiction) {
        const lawValue = typeof law === 'string' ? parseInt(law, 10) : law;
        this.onFieldChange.emit({ jurisdiction, law: lawValue || 0 });
      } else {
        console.warn('Cannot emit law change without a selected jurisdiction');
      }
    });
  }

  loadChecklistGroups(): void {
    const params = new HttpParams().set('size', '9999');
    this.projectsService.listSettingsChecklistGroup({ params }).subscribe({
      next: (response: any) => {
        const groups: any[] = response?.data?.checklistGroups ?? [];
        this.checklistGroupOptions = groups.map((g: any) => ({
          key: g.checklistGroupId,
          value: g.name
        }));
      },
      error: (error) => console.error('Error loading checklist groups:', error)
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['checklist'] && changes['checklist'].currentValue) {
      const checklist = changes['checklist'].currentValue;

      if (checklist.jurisdiction && checklist.jurisdiction.jurisdictionId) {
        const jurisdictionId = checklist.jurisdiction.jurisdictionId;

        let lawId = null;
        if (checklist.law && checklist.law.lawId) {
          lawId = checklist.law.lawId;
        } else if (checklist.lawId) {
          lawId = checklist.lawId;
        } else if (checklist.law && typeof checklist.law === 'number') {
          lawId = checklist.law;
        }

        this.projectsService.getLawsByJurisdiction(jurisdictionId).subscribe({
          next: (laws: any[]) => {
            if (laws && laws.length > 0) {
              this.lawsOptions = laws[0].key !== undefined
                ? laws
                : laws.map(law => ({ key: law.lawId || law.id, value: law.name }));

              setTimeout(() => {
                const checklistGroupId = checklist.checklistGroup?.checklistGroupId ?? null;
                this.selectedChecklistGroupId = checklistGroupId;
                this.form.patchValue({
                  jurisdictionId,
                  law: lawId,
                  checklistGroupId: checklistGroupId ?? '',
                  revision: checklist.revision ?? '',
                  details: checklist.details || '',
                });
                this.onFieldChange.emit({ jurisdiction: jurisdictionId, law: lawId || 0 });
              }, 100);
            } else {
              const checklistGroupId = checklist.checklistGroup?.checklistGroupId ?? null;
              this.selectedChecklistGroupId = checklistGroupId;
              this.form.patchValue({
                jurisdictionId,
                checklistGroupId: checklistGroupId ?? '',
                revision: checklist.revision ?? '',
                details: checklist.details || '',
              });
              this.onFieldChange.emit({ jurisdiction: jurisdictionId, law: 0 });
            }
          },
          error: (error) => {
            console.error('Error loading laws for checklist jurisdiction:', error);
            const checklistGroupId = checklist.checklistGroup?.checklistGroupId ?? null;
            this.selectedChecklistGroupId = checklistGroupId;
            this.form.patchValue({
              jurisdictionId,
              checklistGroupId: checklistGroupId ?? '',
              revision: checklist.revision ?? '',
              details: checklist.details || '',
            });
          }
        });
      }
    }
  }




  /**
     * *******************
     * Submit form methods
     * *******************
     */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formValues = this.form.getRawValue();
      
      const values = {
        jurisdictionId: formValues.jurisdictionId,
        checklistGroupId: this.selectedChecklistGroupId,
        revision: Number(formValues.revision),
        details: formValues.details,
        tenantId: 1,
      };
      
      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.checklist
        ? this.projectsService.updateSettingsChecklist(this.checklist.checklistId, values)
        : this.projectsService.createSettingsChecklist(values);

      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response);
        },
        error: (error: any) => {
          this.submitFormError(error);
        },
      });
    } else {
      console.warn('Form is invalid:', this.form.errors);
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control?.invalid) {
          console.warn(`Field "${key}" invalid:`, control.errors);
        }
      });
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */

  onChecklistGroupChange(id: any): void {
    this.selectedChecklistGroupId = id ? Number(id) : null;
  }

  getProjectJurisdictionSelect(): FormControl { return this.getFormControl('jurisdictionId'); }
  getProjectLawSelect(): FormControl { return this.getFormControl('law'); }
  getChecklistGroupSelect(): FormControl { return this.getFormControl('checklistGroupId'); }
  getProjectRevisionInput(): FormControl { return this.getFormControl('revision'); }
  getProjectDetailsInput(): FormControl { return this.getFormControl('details'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
}
